import React, { useState, useMemo } from "react";
import { useCompanyItems } from "../hooks/useCompanyItems.js";
import { ItemCard } from "../components/ItemCard.jsx";
import { ItemSearch } from "../components/ItemSearch.jsx";
import { ItemForm } from "../components/ItemForm.jsx";
import { BottomSheet } from "../../../shared/components/BottomSheet.jsx";
import { Button } from "../../../shared/components/Button.jsx";
import { useDangerConfirm } from "../../../shared/context/DangerConfirmContext.jsx";
import { useListView } from "../../../shared/hooks/useListView.js";
import { DataTable } from "../../../shared/components/DataTable.jsx";
import { ViewToggle, StickyFooter } from "../../../shared/components/ListChrome.jsx";
import { itemColumns, isLowStock } from "../components/itemColumns.jsx";

export function ItemsPage() {
  const { items, loading, error, addItem, editItem, removeItem } = useCompanyItems();
  const [searchTerm, setSearchTerm] = useState("");
  const [activeFilter, setActiveFilter] = useState("ALL");
  const [isSheetOpen, setIsSheetOpen] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [formSubmitting, setFormSubmitting] = useState(false);
  const { confirmDanger } = useDangerConfirm();

  // Filter and search
  const filteredItems = useMemo(() => {
    return items.filter((item) => {
      const name = (item.itemName || item.name || "").toLowerCase();
      const hsn = (item.gstCode || item.sku || "").toLowerCase();
      const term = searchTerm.toLowerCase().trim();

      const matchSearch = !term || name.includes(term) || hsn.includes(term);
      if (!matchSearch) return false;

      if (activeFilter === "GOODS") {
        return item.itemType !== "service";
      }
      if (activeFilter === "SERVICES") {
        return item.itemType === "service";
      }
      if (activeFilter === "LOW_STOCK") {
        return item.itemType !== "service" && Number(item.stock) <= Number(item.reorderLevel || 10);
      }

      return true;
    });
  }, [items, searchTerm, activeFilter]);

  const handleOpenCreate = () => {
    setEditingItem(null);
    setIsSheetOpen(true);
  };

  const listView = useListView("items", { onNew: handleOpenCreate });
  const lowStockCount = useMemo(() => items.filter(isLowStock).length, [items]);
  const columns = useMemo(() => itemColumns({
    onDelete: async (item) => {
      if (await confirmDanger({ title: "Delete item?", message: `Delete "${item.itemName || item.name}"?` })) {
        removeItem(item.id);
      }
    }
  }), [confirmDanger, removeItem]);

  const handleOpenEdit = (item) => {
    setEditingItem(item);
    setIsSheetOpen(true);
  };

  const handleCloseSheet = () => {
    setIsSheetOpen(false);
    setEditingItem(null);
  };

  const handleFormSubmit = async (formData) => {
    setFormSubmitting(true);
    try {
      if (editingItem) {
        await editItem(editingItem.id, formData);
      } else {
        await addItem(formData);
      }
      handleCloseSheet();
    } finally {
      setFormSubmitting(false);
    }
  };

  return (
    <div className="items-page">
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "1rem", gap: "0.5rem", width: "100%" }}>
        <div style={{ minWidth: 0, flex: 1 }}>
          <h2 style={{ fontSize: "1.25rem", fontWeight: "700" }}>Items</h2>
          <p style={{ fontSize: "0.8rem", color: "var(--text-dim)", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
            {filteredItems.length} {filteredItems.length === 1 ? "item" : "items"} (Goods & Services)
          </p>
        </div>
        <Button size="sm" variant="primary" onClick={handleOpenCreate} style={{ flexShrink: 0 }}>
          Add Item
        </Button>
      </div>

      <ItemSearch
        searchTerm={searchTerm}
        onSearchChange={setSearchTerm}
        activeFilter={activeFilter}
        onFilterChange={setActiveFilter}
      >
        <ViewToggle view={listView.view} onChange={listView.setView} />
      </ItemSearch>

      {loading && (
        <div className="state-box">
          <div className="state-icon">⏳</div>
          <div className="state-title">Loading Items...</div>
        </div>
      )}

      {error && (
        <div className="state-box">
          <div className="state-icon">⚠️</div>
          <div className="state-title">Error Loading Items</div>
          <div className="state-desc">{error}</div>
        </div>
      )}

      {!loading && !error && filteredItems.length === 0 && (
        <div className="state-box">
          <div className="state-icon">📦</div>
          <div className="state-title">No items found</div>
          <div className="state-desc">
            {searchTerm ? "Try searching with a different product name or HSN code." : "Get started by adding your first product or service item."}
          </div>
          <Button variant="primary" onClick={handleOpenCreate}>
            Add First Item
          </Button>
        </div>
      )}

      {listView.showTable ? (
        <DataTable
          columns={columns}
          rows={filteredItems}
          onRowClick={handleOpenEdit}
          storageKey="items"
          noun="items"
          resetKey={`${activeFilter}|${searchTerm}`}
        />
      ) : (
        <div className="items-list card-grid">
          {filteredItems.map((item) => (
            <ItemCard
              key={item.id}
              item={item}
              onEdit={handleOpenEdit}
              onDelete={removeItem}
            />
          ))}
        </div>
      )}

      <StickyFooter
        shown={filteredItems.length}
        total={items.length}
        noun="items"
        chips={[{ label: "Low stock", value: lowStockCount, tone: lowStockCount > 0 ? "danger" : "success" }]}
      />

      {/* Floating Action Button */}
      <button
        type="button"
        className="fab"
        onClick={handleOpenCreate}
        aria-label="Add Item"
      >
        +
      </button>

      {/* Slide-Up Bottom Sheet for Add/Edit */}
      <BottomSheet
        isOpen={isSheetOpen}
        onClose={handleCloseSheet}
        title={editingItem ? "Edit Item" : "New Item / Product"}
      >
        <ItemForm
          initialData={editingItem}
          onSubmit={handleFormSubmit}
          onCancel={handleCloseSheet}
          loading={formSubmitting}
        />
      </BottomSheet>
    </div>
  );
}

export default ItemsPage;
